import { useState, useEffect, useRef, useCallback, type KeyboardEvent } from 'react';
import { Check, Loader, ChevronLeft, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui-kit/button';
import { getBlockLabel } from './utils';
import type { ReviewBlock } from './adapter/types';

interface SidebarBlockEditorProps {
  block: ReviewBlock;
  initialText: string;
  onSave: (newText: string) => Promise<void>;
  onCancel: () => void;
  onDirtyChange?: (dirty: boolean) => void;
  onTextChange?: (text: string) => void;
  isSaving: boolean;
  isEffectiveDate: boolean;
}

/**
 * In-sidebar block editor. Replaces the old modal InlineTextEditor — editing
 * now happens inline in the right panel's Edit tab while the document stays
 * visible. Reports dirty/text state up so the parent can guard block switches.
 */
export function SidebarBlockEditor({
  block,
  initialText,
  onSave,
  onCancel,
  onDirtyChange,
  onTextChange,
  isSaving,
  isEffectiveDate,
}: SidebarBlockEditorProps) {
  const { t } = useTranslation();
  const [text, setText] = useState(initialText);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.userAgent);
  const modKey = isMac ? '⌘' : 'Ctrl+';

  const blockType = isEffectiveDate ? 'effectiveDate' : block.kind;
  const { label: typeLabel, Icon: BlockIcon } = getBlockLabel(t, blockType, block.isTitle);
  // Virtual blocks (title, signature fields) carry a friendlier explicit label.
  const blockLabel = block.label ?? typeLabel;

  const isChanged = text.trim() !== initialText.trim();

  useEffect(() => {
    onDirtyChange?.(isChanged);
  }, [isChanged, onDirtyChange]);

  useEffect(() => {
    onTextChange?.(text);
  }, [text, onTextChange]);

  const resizeTextarea = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.style.height = '0px';
    const scrollHeight = textarea.scrollHeight;
    const minHeight = block.isTitle ? 60 : 120;
    const maxHeight = 400;
    textarea.style.height = `${Math.max(minHeight, Math.min(scrollHeight, maxHeight))}px`;
  }, [block.isTitle]);

  useEffect(() => {
    setText(initialText);
  }, [initialText, block.id]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        textareaRef.current.select();
        resizeTextarea();
      }
    }, 50);
    return () => clearTimeout(timer);
  }, [block.id, resizeTextarea]);

  useEffect(() => {
    resizeTextarea();
  }, [text, resizeTextarea]);

  const handleSave = useCallback(async () => {
    if (isChanged && !isSaving) {
      await onSave(text.trim());
    } else {
      onCancel();
    }
  }, [isChanged, isSaving, onSave, text, onCancel]);

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onCancel();
      return;
    }
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault();
      void handleSave();
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-border">
        <button
          onClick={onCancel}
          className="p-1 rounded-md hover:bg-muted text-globe-grey transition-colors"
          aria-label={t('GENERATEREVIEW_EDITOR_BACK', { defaultValue: 'Back to outline' })}
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <div className="w-6 h-6 rounded bg-selise-blue/10 flex items-center justify-center shrink-0">
            <BlockIcon className="w-3.5 h-3.5 text-selise-blue" />
          </div>
          <span className="text-sm font-semibold text-[hsl(var(--fg))] truncate">{blockLabel}</span>
        </div>
        <button
          onClick={onCancel}
          className="p-1 rounded-md hover:bg-muted text-globe-grey transition-colors"
          aria-label={t('GENERATEREVIEW_EDITOR_CLOSE', { defaultValue: 'Close editor' })}
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Editor */}
      <div className="flex-1 overflow-y-auto px-4 py-4">
        {isEffectiveDate && (
          <p className="text-xs text-globe-grey mb-3 leading-relaxed">
            {t('GENERATEREVIEW_EDITOR_DATEHINT', {
              defaultValue: 'Format: "Label: value" (e.g., "Effective Date: January 15, 2024")',
            })}
          </p>
        )}
        <textarea
          ref={textareaRef}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={isSaving}
          aria-label={`Edit ${blockLabel}`}
          className="w-full resize-none border border-border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-selise-blue focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed bg-[hsl(var(--bg))]"
          placeholder={`Edit ${blockLabel.toLowerCase()}...`}
          style={{
            fontFamily: 'inherit',
            lineHeight: 1.7,
            fontWeight: block.isTitle ? 600 : 400,
            overflowY: 'auto',
          }}
        />
        <div className="flex items-center justify-between mt-2">
          <span className="text-[11px] text-globe-grey">
            {isChanged ? (
              <span className="text-selise-blue">
                {t('GENERATEREVIEW_EDITOR_MODIFIED', { defaultValue: 'Modified' })}
              </span>
            ) : (
              t('GENERATEREVIEW_EDITOR_NOCHANGES', { defaultValue: 'No changes' })
            )}
          </span>
          <span className="text-[11px] text-globe-grey">
            <kbd className="px-1 py-0.5 bg-muted rounded text-[10px]">{modKey}↵</kbd>{' '}
            {t('GENERATEREVIEW_EDITOR_SAVEKBD', { defaultValue: 'save' })}
            {' · '}
            <kbd className="px-1 py-0.5 bg-muted rounded text-[10px]">esc</kbd>{' '}
            {t('GENERATEREVIEW_EDITOR_CANCELKBD', { defaultValue: 'cancel' })}
          </span>
        </div>
      </div>

      {/* Actions */}
      <div className="shrink-0 px-4 py-3 border-t border-border space-y-2 flex gap-4 flex-row-reverse">
        <Button onClick={handleSave} disabled={isSaving || !isChanged} variant="primary" size="sm">
          {isSaving ? (
            <>
              <Loader className="w-3.5 h-3.5 mr-1.5 animate-spin" />
              {t('GENERATEREVIEW_EDITOR_SAVING', { defaultValue: 'Saving...' })}
            </>
          ) : (
            <>
              <Check className="w-3.5 h-3.5 mr-1.5" />
              {t('GENERATEREVIEW_EDITOR_SAVE', { defaultValue: 'Save Changes' })}
            </>
          )}
        </Button>
        <Button variant="outline" onClick={onCancel} disabled={isSaving} size="sm">
          {t('GENERATEREVIEW_EDITOR_CANCEL', { defaultValue: 'Cancel' })}
        </Button>
      </div>
    </div>
  );
}
